import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismIcon,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, not } from 'ember-truth-helpers';
import type { UploadOptions } from 'lumen/components/settings/budget-forecast/upload-download-modal';
import {
  DISTRIBUTION_METHODS,
  type DistributionMethod,
} from 'lumen/utils/constants/budget-forecast-constants';

interface SettingsBudgetForecastUploadDownloadModalCompleteUploadSignature {
  Args: {
    updateUploadOptions: (options: UploadOptions) => void;
    uploadOptions: UploadOptions;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastUploadDownloadModalCompleteUpload extends Component<SettingsBudgetForecastUploadDownloadModalCompleteUploadSignature> {
  @service declare intl: Services['intl'];

  get distributionMethodOptions(): {
    label: string;
    value: DistributionMethod;
  }[] {
    return [
      {
        label: this.intl.t('budget_forecast.distribution.equally'),
        value: DISTRIBUTION_METHODS.EQUAL,
      },
      {
        label: this.intl.t('budget_forecast.distribution.ly_finals'),
        value: DISTRIBUTION_METHODS.LAST_YEAR_FINALS,
      },
    ];
  }

  @action
  onDistributionMethodChange(event: Event): void {
    const value = (event.target as HTMLInputElement)
      .value as DistributionMethod;
    this.args.updateUploadOptions({
      ...this.args.uploadOptions,
      distributionMethod: value,
    });
  }

  <template>
    {{#if @uploadOptions.isTooManyFiles}}
      <PrismBanner
        @bannerMessage={{if
          (eq @view "budget")
          (t "budget_forecast.upload_budget.too_many_files")
          (t "budget_forecast.upload_forecast.too_many_files")
        }}
        @bannerStyle="info"
        @icon="circle-information-outline"
      />
    {{/if}}
    {{#unless @uploadOptions.isValid}}
      <PrismBanner
        @bannerMessage={{if
          (eq @view "budget")
          (t "budget_forecast.upload_budget.invalid_file_type")
          (t "budget_forecast.upload_forecast.invalid_file_type")
        }}
        @bannerStyle="critical"
        @icon="warning"
      />
    {{/unless}}
    <div class="bg-neutral-subdued flex gap-200 px-400 py-500">
      <PrismIcon @name="file" />
      <div
        class="heading-4 mr-auto truncate"
        title={{if @uploadOptions.file @uploadOptions.file.name ""}}
      >
        {{#if @uploadOptions.file}}
          {{@uploadOptions.file.name}}
        {{else}}
          {{t "general.missing"}}
        {{/if}}
      </div>
      {{#if @uploadOptions.isValid}}
        <PrismIcon @color="success" @name="circle-checkmark" />
      {{else}}
        <PrismIcon @color="critical" @name="circle-cross" />
      {{/if}}
    </div>

    <div class="flex flex-col gap-200">
      <label class="text-300--medium">
        {{t "budget_forecast.distribution.label"}}
      </label>
      <PrismRadioGroup
        @disabled={{not @uploadOptions.isValid}}
        @isHorizontal={{true}}
        @name="distribution-method"
        @onChange={{this.onDistributionMethodChange}}
        @value={{@uploadOptions.distributionMethod}}
        as |RadioGroup|
      >
        {{#each this.distributionMethodOptions as |method|}}
          <RadioGroup.input @value={{method.value}}>
            {{method.label}}
          </RadioGroup.input>
        {{/each}}
      </PrismRadioGroup>
    </div>
  </template>
}
